'use client';
// AmanChain — Developers & Integrations
// Everything an external platform (exchange, wallet, explorer, payment
// processor) needs to integrate with AmanChain: network parameters, the
// public REST API, the AMT-20 / stable / DEX standards and the asset
// registry. Every number here is the real consensus constant.
import { useState } from 'react';
import {
  Code2, Terminal, Network, Copy, Check, ShieldCheck, Layers, Coins,
  Droplets, KeyRound, ListChecks, Globe, Zap, Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import { SectionHeader, Mono } from './shared';
import type { Net } from './types';
import { ESSENTIALS } from '@/lib/chain/essentials';

const ENDPOINTS: { method: 'GET' | 'POST'; path: string; desc: string; params?: string; curl?: string }[] = [
  {
    method: 'GET', path: '/api/v1/health',
    desc: 'Liveness probe + the network registry (which networks exist and are launched).',
    curl: `curl https://<node>/api/v1/health`,
  },
  {
    method: 'GET', path: '/api/v1/chain',
    desc: 'Full network summary: height, supply, burned, difficulty, transaction counts, the live AMAN market ticker and the effective governance parameters.',
    curl: `curl "https://<node>/api/v1/chain?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/blocks',
    desc: 'Paginated block list (newest first), or a single block by height or hash.',
    params: 'offset · limit (max 50) · height · hash',
    curl: `curl "https://<node>/api/v1/blocks?net=mainnet&offset=0&limit=20"`,
  },
  {
    method: 'GET', path: '/api/v1/blocks/:height',
    desc: 'One block with its full transaction list, merkle root, difficulty, nonce and miner.',
    curl: `curl "https://<node>/api/v1/blocks/448?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/txs',
    desc: 'Paginated transaction feed (mined + mempool), optionally filtered by address.',
    params: 'offset · limit · address',
    curl: `curl "https://<node>/api/v1/txs?net=mainnet&address=AM…"`,
  },
  {
    method: 'GET', path: '/api/v1/txs/:id',
    desc: 'Full transaction by id — including the complete quantum-shield signature hex.',
    curl: `curl "https://<node>/api/v1/txs/<txid>?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/addresses/:address',
    desc: 'Account view: AMAN balance, nonce, staking positions, token holdings, LP liquidity and founder flag.',
    curl: `curl "https://<node>/api/v1/addresses/AM…?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/tokens',
    desc: 'Every deployed AMT-20 token: metadata, decimals, supply, burned, holders, stable flag, peg, logo (data URL).',
    curl: `curl "https://<node>/api/v1/tokens?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/tokens/:symbol',
    desc: 'Token detail: top holders, transfer history, mint/burn ledger and vault collateral for stables.',
    curl: `curl "https://<node>/api/v1/tokens/USDC?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/pools',
    desc: 'Real AMM pool state: reserves, spot price, TVL, 24h volume, LP supply, fee split stats and recent executed swaps.',
    params: 'address (optional — adds the caller LP positions)',
    curl: `curl "https://<node>/api/v1/pools?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/price',
    desc: 'The canonical AMAN price — spot of the deepest pool, derived purely from consensus reserves. Includes 24h change and TVL.',
    curl: `curl "https://<node>/api/v1/price?net=mainnet"`,
  },
  {
    method: 'GET', path: '/api/v1/tx?address=AM…',
    desc: 'External-signer helper: current nonce, pending count and the canonical signing message layout.',
    curl: `curl "https://<node>/api/v1/tx?net=mainnet&address=AM…"`,
  },
  {
    method: 'POST', path: '/api/v1/tx',
    desc: 'Broadcast a FULLY SIGNED transaction from your own infrastructure. The node verifies the hybrid signature (Ed25519 + ML-DSA-87) and the id/signature binding, then admits it to the mempool. Rate limit: 30/min.',
    params: 'body = complete signed transaction object (64 KB limit)',
    curl: `curl -X POST "https://<node>/api/v1/tx?net=mainnet" \\\n  -H 'Content-Type: application/json' -d @signed-tx.json`,
  },
];

function CopyBtn({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          toast.success('Copied to clipboard');
          setTimeout(() => setDone(false), 1500);
        }).catch(() => toast.error('Clipboard unavailable'));
      }}
      className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted/60 transition"
      aria-label="Copy example"
    >
      {done ? <Check className="size-3.5 text-emerald-400" /> : <Copy className="size-3.5" />}
    </button>
  );
}

export function Developers({ net }: { net: Net }) {
  return (
    <div className="space-y-6">
      <SectionHeader
        icon={Code2}
        title="Developers & Integrations"
        desc="The integration surface global platforms build on: public REST API, quantum-safe signing, AMT-20 token standard, fully-backed stables and the on-chain AMM — no allowlist, open CORS, real-time consensus data."
      />

      {/* Quickstart */}
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
        <QuickCard icon={Globe} title="Base URL" accent="text-emerald-400">
          <Mono className="text-xs break-all">https://&lt;your-node&gt;/api/v1</Mono>
          <p className="text-[0.68rem] text-muted-foreground mt-1">Every endpoint below is relative to this base. Responses are JSON with <Mono>Content-Type: application/json</Mono>.</p>
        </QuickCard>
        <QuickCard icon={Network} title="Networks" accent="text-sky-400">
          <Mono className="text-xs">?net=mainnet · ?net=testnet</Mono>
          <p className="text-[0.68rem] text-muted-foreground mt-1">Chain-ID 1 = Mainnet, Chain-ID 2 = Testnet. Alternatively send the <Mono>x-aman-net</Mono> header. Default: mainnet.</p>
        </QuickCard>
        <QuickCard icon={Zap} title="Rate limits" accent="text-amber-400">
          <Mono className="text-xs">120 req/min · scope</Mono>
          <p className="text-[0.68rem] text-muted-foreground mt-1">Per IP per endpoint scope, sliding window. Broadcast (<Mono>POST /api/v1/tx</Mono>) is 30/min. 429 answers with <Mono>retryAfter</Mono>.</p>
        </QuickCard>
        <QuickCard icon={ShieldCheck} title="CORS & security" accent="text-violet-400">
          <Mono className="text-xs">Access-Control-Allow-Origin: *</Mono>
          <p className="text-[0.68rem] text-muted-foreground mt-1">Open for <Mono>/api/v1/*</Mono> with preflight — call it from browsers, servers or apps. Hardened security headers on every response.</p>
        </QuickCard>
      </div>

      {/* Network parameters */}
      <section className="glass p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold"><Network className="size-4 text-emerald-400" /> Network parameters (consensus constants)</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <Param k="Chain-IDs" v="1 = Mainnet · 2 = Testnet" />
          <Param k="Consensus" v="Proof-of-Work, SHA3-256 (Keccak)" />
          <Param k="Signature shield" v="Ed25519 + ML-DSA-87 (NIST FIPS 204, category 5) — dual, mandatory" />
          <Param k="Address format" v="AM + 40 hex + 8 checksum (SHA3-512 of both public keys)" />
          <Param k="Block reward" v="40 AMAN, halving every 50,000 blocks" />
          <Param k="Hard cap" v="100,000,000 AMAN (deflationary fee burns on top)" />
          <Param k="Fee burn" v="50% of every transaction fee destroyed forever" />
          <Param k="Token deploy fee" v="25 AMAN burned 100% per AMT-20 deployment" />
          <Param k="Swap fee" v="0.30% per swap — 1/6 burned, 5/6 to liquidity providers" />
          <Param k="Block capacity" v="20 transactions per block · mempool 300" />
          <Param k="Stable standard" v="100% AMAN-collateral — mint locks, redeem releases, enforced in consensus" />
          <Param k="Staking" v="Flex 6% APR · 90-day 9% · 365-day 15%, on-chain tiers" />
        </div>
      </section>

      {/* API reference */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold px-1"><Terminal className="size-4 text-emerald-400" /> Public API reference</div>
        <div className="space-y-2">
          {ENDPOINTS.map(e => <ApiRow key={e.method + e.path} e={e} />)}
        </div>
      </section>

      {/* Agent-native APIs (L0-L4) */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold px-1"><Bot className="size-4 text-violet-400" /> Agent-native APIs — make your AI a chain citizen</div>
        <div className="grid lg:grid-cols-3 gap-3">
          <AgentApiCard
            title="MCP Server" tag="JSON-RPC 2.0" tagTone="violet"
            desc="Model Context Protocol endpoint for AI runtimes (Claude, GPT, custom agents). Eleven real tools: chain info, live price, the service marketplace, x402 invocation, token audits, the autopilot status and more. Point any MCP client at the URL — no API key needed for reads."
            curl={`curl -X POST "https://<node>/api/mcp?net=mainnet" \\\n  -H 'Content-Type: application/json' \\\n  -d '{"jsonrpc":"2.0","id":1,"method":"tools/call",\n       "params":{"name":"aman_autopilot_status","arguments":{}}}'`}
          />
          <AgentApiCard
            title="AmanPay x402" tag="HTTP 402" tagTone="amber"
            desc="Per-request payments between agents, settled on-chain. Call without payment → the API answers 402 with the exact price and pay-to address. Submit an agent_invoke transaction, then retry with its txId → the node verifies the real settlement and executes the service."
            curl={`# 1) ask — returns 402 + requirements\ncurl -X POST "https://<node>/api/x402?net=mainnet" \\\n  -H 'Content-Type: application/json' \\\n  -d '{"serviceId":"aman-price-feed"}'\n\n# 2) pay via /api/tx (agent_invoke), then:\ncurl -X POST "https://<node>/api/x402?net=mainnet" \\\n  -H 'Content-Type: application/json' \\\n  -d '{"serviceId":"aman-price-feed",\n       "payment":{"txId":"<txid>"}}'`}
          />
          <AgentApiCard
            title="Real-time Stream" tag="SSE" tagTone="sky"
            desc="Server-Sent Events feed of the live chain: every new block, transfer, swap and x402 agent payment, pushed the moment it is mined. Perfect for agents that need to react to the world instantly without polling."
            curl={`curl -N "https://<node>/api/stream?net=mainnet"\n\n# event: x402_call\ndata: {"height":512,"serviceId":"aman-price-feed",\n       "amount":0.05,"from":"AM…"}\n\n# event: block\ndata: {"height":513,"difficulty":5,"txs":2}`}
          />
        </div>
        <div className="rounded-lg bg-violet-500/5 ring-1 ring-violet-500/20 p-3 text-[0.7rem] text-muted-foreground">
          <b className="text-violet-400">Agent economy stack:</b> agent identities (<Mono>agent_register</Mono>), services with fixed per-call prices (<Mono>agent_service</Mono>) and x402 settlements (<Mono>agent_invoke</Mono>) are consensus objects — every agent-to-agent payment is a real signed transaction mined into a block. List everything on <Mono>GET /api/agents?net=</Mono>.
        </div>
        <div className="rounded-lg bg-amber-500/5 ring-1 ring-amber-500/20 p-3 text-[0.7rem] text-muted-foreground">
          <b className="text-amber-400">Agent Auto-Pilot (the closed loop):</b> the node operates a keeper agent (<Mono>@aman-keeper</Mono>) that invokes real services on a schedule and settles every call with its own x402 payment — agent pays agent, automatically, inside consensus. Watch it live in the AI Agents tab, or read <Mono>GET /api/agents?net=</Mono> → <Mono>autopilot</Mono>. Users can run their own autopilot from the same tab with a hard daily budget enforced by their wallet session.
        </div>
      </section>

      {/* Standards */}
      <div className="grid lg:grid-cols-2 gap-4">
        <section className="glass p-5 space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-bold"><Coins className="size-4 text-amber-400" /> AMT-20 token standard</div>
          <p className="text-xs text-muted-foreground">Native tokens live inside consensus — no EVM, no bridging. Deployment burns 25 AMAN. Fields: <Mono>tokenName · symbol · decimals (0–8) · maxSupply · initialSupply · description · website · logo</Mono> (data-URL, ≤120K chars, stored in the chain).</p>
          <ul className="text-xs space-y-1.5 list-disc pl-4">
            <li><Mono>token_create</Mono> — deploy (fee burned), initial supply goes to the creator</li>
            <li><Mono>token_mint</Mono> — creator only, capped by <Mono>maxSupply</Mono></li>
            <li><Mono>token_burn</Mono> — any holder, permanently reduces supply</li>
            <li><Mono>token_transfer</Mono> — standard movement between addresses</li>
          </ul>
          <div className="rounded-lg bg-muted/40 ring-1 ring-border p-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[0.65rem] font-bold text-muted-foreground">Example — deploy a token</span>
              <CopyBtn text={JSON.stringify(TOKEN_EXAMPLE, null, 2)} />
            </div>
            <pre className="text-[0.62rem] leading-relaxed overflow-x-auto" dir="ltr">{JSON.stringify(TOKEN_EXAMPLE, null, 2)}</pre>
          </div>
        </section>

        <section className="glass p-5 space-y-2.5">
          <div className="flex items-center gap-2 text-sm font-bold"><Layers className="size-4 text-emerald-400" /> Stable-token standard & DEX</div>
          <p className="text-xs text-muted-foreground">A stable AMT-20 sets <Mono>stable: true</Mono> and <Mono>pegAman</Mono> (AMAN per unit). Anyone can <Mono>stable_mint</Mono> by locking exactly <Mono>pegAman × amount</Mono> AMAN in the consensus vault, and <Mono>stable_redeem</Mono> burns units to release collateral — the 100%-backing invariant is checked by every node, so the market price is arbitrage-pinned to the peg.</p>
          <ul className="text-xs space-y-1.5 list-disc pl-4">
            <li>DEX pairs are <Mono>AMAN / token</Mono> constant-product pools (<Mono>x·y=k</Mono>) validated in consensus</li>
            <li>Quote locally from <Mono>/api/v1/pools</Mono> reserves, or trust <Mono>/api/v1/price</Mono> for the canonical AMAN spot</li>
            <li>Swaps are signed txs: <Mono>{`{ poolId, direction: 'aman_to_token' | 'token_to_aman', amount, minOut, deadline }`}</Mono></li>
            <li><Mono>minOut</Mono> + <Mono>deadline</Mono> revert protection is enforced by the chain, not the client</li>
            <li>LP units are consensus objects — <Mono>pool_add</Mono> / <Mono>pool_remove</Mono> mint/burn them proportionally</li>
          </ul>
        </section>
      </div>

      {/* Essentials registry */}
      <section className="glass p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold"><Layers className="size-4 text-sky-400" /> Network Essentials — canonical asset registry</div>
        <p className="text-xs text-muted-foreground">The standard asset set deployed on-chain by the network issuer, with official logos and seeded AMM markets. Global platforms can rely on these symbols as the deep, canonical markets of AmanChain.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border/70">
                <th className="py-2 pr-3 font-bold">Asset</th>
                <th className="py-2 pr-3 font-bold">Type</th>
                <th className="py-2 pr-3 font-bold">Decimals</th>
                <th className="py-2 pr-3 font-bold">Backing</th>
                <th className="py-2 font-bold">Market</th>
              </tr>
            </thead>
            <tbody>
              {ESSENTIALS.map(a => (
                <tr key={a.symbol} className="border-b border-border/40 last:border-0">
                  <td className="py-2 pr-3"><span className="font-black" style={{ color: a.color }}>{a.symbol}</span> <span className="text-muted-foreground">— {a.name}</span></td>
                  <td className="py-2 pr-3">{a.kind === 'stable' ? 'Stable (AMT-20)' : 'Wrapped (AMT-20)'}</td>
                  <td className="py-2 pr-3" dir="ltr">{a.decimals}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{a.kind === 'stable' ? '100% AMAN escrow (consensus vault)' : 'Network issuance · AMM-priced'}</td>
                  <td className="py-2"><Mono>AMAN-{a.symbol}</Mono></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* External signer flow */}
      <section className="glass p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold"><KeyRound className="size-4 text-violet-400" /> External signing (custody-friendly)</div>
        <div className="grid md:grid-cols-4 gap-2 text-xs">
          <Step n={1} title="Fetch nonce" body="GET /api/v1/tx?address=AM… → current nonce + chainId." />
          <Step n={2} title="Build the message" body="Canonical layout: AMAN-v2|chainId|type|from|to|amount|fee|nonce|timestamp|data|tier." />
          <Step n={3} title="Dual-sign it" body="Sign once with Ed25519 and once with ML-DSA-87 (FIPS 204) from your HSM/custody stack." />
          <Step n={4} title="Broadcast" body="POST /api/v1/tx — the node verifies both signatures and the id binding before admitting it." />
        </div>
        <p className="text-[0.68rem] text-muted-foreground">Keys are derived from a single hybrid seed: Ed25519 for speed, ML-DSA-87 for post-quantum security — every consensus node rejects any transaction missing either signature.</p>
      </section>

      {/* Listing checklist */}
      <section className="glass p-5 space-y-3">
        <div className="flex items-center gap-2 text-sm font-bold"><ListChecks className="size-4 text-emerald-400" /> Listing checklist for exchanges & data platforms</div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
          <CheckRow body="Supply & cap: /api/v1/chain (totalSupply, totalBurned, height)" />
          <CheckRow body="Canonical price feed: /api/v1/price (AMM reserve spot + 24h change)" />
          <CheckRow body="Asset registry: /api/v1/tokens (+ /tokens/:symbol for holders & vault)" />
          <CheckRow body="Market data: /api/v1/pools (TVL, 24h volume, executed swaps)" />
          <CheckRow body="Deposits: watch /api/v1/txs?address=<hot wallet>" />
          <CheckRow body="Withdrawals: POST /api/v1/tx from your signed infrastructure" />
          <CheckRow body="Chain-ID isolation: 1 mainnet · 2 testnet (replay-protected)" />
          <CheckRow body="Explorer verification: every tx carries its full quantum-shield hex" />
        </div>
        <div className="rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/20 p-3 text-[0.7rem] text-muted-foreground">
          <b className="text-emerald-400">Quantum-safe statement:</b> every transaction on AmanChain is signed with Ed25519 <i>and</i> ML-DSA-87 (NIST FIPS 204, highest security category 5). Consensus rejects anything less — platforms integrating today are harvest-now-decrypt-later safe by construction.
        </div>
      </section>
    </div>
  );
}

const TOKEN_EXAMPLE = {
  type: 'token_create',
  tokenName: 'Example Asset',
  symbol: 'EXA',
  decimals: 8,
  maxSupply: 1000000,
  initialSupply: 100000,
  description: 'Deployed via the public broadcast API',
  website: 'https://example.com',
};

function QuickCard({ icon: Icon, title, accent, children }: {
  icon: React.ComponentType<{ className?: string }>; title: string; accent: string; children: React.ReactNode;
}) {
  return (
    <div className="glass p-4 space-y-1.5">
      <div className="flex items-center gap-2 text-sm font-bold"><Icon className={`size-4 ${accent}`} /> {title}</div>
      {children}
    </div>
  );
}

function Param({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded-lg bg-muted/40 ring-1 ring-border/70 px-3 py-2">
      <div className="text-[0.62rem] text-muted-foreground font-bold uppercase tracking-wide">{k}</div>
      <div className="text-xs font-bold mt-0.5">{v}</div>
    </div>
  );
}

function ApiRow({ e }: { e: { method: 'GET' | 'POST'; path: string; desc: string; params?: string; curl?: string } }) {
  return (
    <div className="rounded-xl ring-1 ring-border bg-muted/20 p-3.5 space-y-1.5">
      <div className="flex items-start gap-2 flex-wrap">
        <span className={`rounded-md px-1.5 py-0.5 text-[0.6rem] font-black ${e.method === 'GET' ? 'bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30' : 'bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30'}`}>
          {e.method}
        </span>
        <Mono className="text-xs font-bold break-all">{e.path}</Mono>
      </div>
      <p className="text-[0.7rem] text-muted-foreground">{e.desc}</p>
      {e.params && <p className="text-[0.65rem]"><span className="font-bold text-muted-foreground">Params: </span><Mono className="text-[0.65rem]">{e.params}</Mono></p>}
      {e.curl && (
        <div className="flex items-start justify-between gap-2 rounded-lg bg-muted/70 ring-1 ring-border/60 px-2.5 py-2 mt-1">
          <pre className="text-[0.62rem] text-emerald-300/90 leading-relaxed overflow-x-auto flex-1" dir="ltr">{e.curl}</pre>
          <CopyBtn text={e.curl.replace(/\\\n\s*/g, ' ')} />
        </div>
      )}
    </div>
  );
}

function AgentApiCard({ title, tag, tagTone, desc, curl }: {
  title: string; tag: string; tagTone: 'violet' | 'amber' | 'sky'; desc: string; curl: string;
}) {
  const tones = {
    violet: 'bg-violet-500/10 text-violet-400 ring-violet-500/30',
    amber: 'bg-amber-500/10 text-amber-400 ring-amber-500/30',
    sky: 'bg-sky-500/10 text-sky-400 ring-sky-500/30',
  };
  return (
    <div className="glass p-4 space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-bold">{title}</span>
        <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-black ring-1 ${tones[tagTone]}`}>{tag}</span>
      </div>
      <p className="text-[0.7rem] text-muted-foreground leading-relaxed">{desc}</p>
      <div className="flex items-start justify-between gap-2 rounded-lg bg-muted/70 ring-1 ring-border/60 px-2.5 py-2">
        <pre className="text-[0.6rem] text-emerald-300/90 leading-relaxed overflow-x-auto flex-1" dir="ltr">{curl}</pre>
        <CopyBtn text={curl.replace(/\\\n\s*/g, ' ')} />
      </div>
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <div className="rounded-lg bg-muted/40 ring-1 ring-border/70 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="size-5 rounded-full bg-primary/15 text-emerald-400 ring-1 ring-primary/40 inline-flex items-center justify-center text-[0.6rem] font-black">{n}</span>
        <span className="font-bold">{title}</span>
      </div>
      <p className="text-muted-foreground">{body}</p>
    </div>
  );
}

function CheckRow({ body }: { body: string }) {
  return (
    <div className="flex items-start gap-2">
      <Check className="size-3.5 text-emerald-400 mt-0.5 shrink-0" />
      <span className="text-muted-foreground">{body}</span>
    </div>
  );
}
